import { useTranslations } from "@jugl-web/utils";
import { FilterSectionLayout } from "@web-src/modules/filters/components/FilterSectionLayout";
import { FilterPill } from "@web-src/modules/filters/components/FilterPill";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { CustomerPicker } from "@web-src/modules/customers/components/CustomerPicker";
import { Customer } from "@jugl-web/rest-api";
import { useCustomer } from "@jugl-web/domain-resources/customers/hooks/useCustomer";
import { Avatar, ListBoxItem } from "@jugl-web/ui-kit";
import { useFilters } from "../../hooks/useFilters";

interface ComponentProps {
  title: string;
  filters: ReturnType<typeof useFilters>;
}

export const TypeCustomerPicker = ({ title, filters }: ComponentProps) => {
  const { t } = useTranslations();
  const { entity } = useEntitySelectedProvider();
  const { filterState, setFilter, clearFilter } = filters;

  const selectedCustomerIds = filterState[title] || [];

  const handleCustomerSelect = (
    customer: ListBoxItem<Customer | null> | null
  ) => {
    if (customer && customer.id) {
      const currentCustomers = filterState[title] || [];
      if (!currentCustomers.includes(customer.id)) {
        setFilter(title, [...currentCustomers, customer.id]);
      }
    }
  };

  const handleRemoveCustomer = (customerId: string) => {
    const newSelections = selectedCustomerIds.filter(
      (id: string) => id !== customerId
    );
    if (newSelections.length === 0) {
      clearFilter(title);
    } else {
      setFilter(title, newSelections);
    }
  };

  return (
    <FilterSectionLayout>
      <FilterSectionLayout.Header
        title={title}
        endSlot={
          <CustomerPicker
            entityId={entity.id}
            onChange={handleCustomerSelect}
            value={null}
            triggerType="button"
            defaultSelectedIds={selectedCustomerIds}
            addButtonLabel={t({ id: "common.add", defaultMessage: "Add" })}
          />
        }
      />
      {selectedCustomerIds.length > 0 && (
        <FilterSectionLayout.PillsContainer>
          {selectedCustomerIds.map((customerId: string) => (
            <CustomerPill
              key={customerId}
              customerId={customerId}
              entityId={entity.id}
              onRemove={() => handleRemoveCustomer(customerId)}
            />
          ))}
        </FilterSectionLayout.PillsContainer>
      )}
    </FilterSectionLayout>
  );
};

interface CustomerPillProps {
  customerId: string;
  entityId: string;
  onRemove: () => void;
}

const CustomerPill = ({
  customerId,
  entityId,
  onRemove,
}: CustomerPillProps) => {
  const { safeCustomerDetails } = useCustomer({
    entityId,
    customerId,
  });

  const customerName = safeCustomerDetails?.fullName || customerId;

  return (
    <FilterPill
      label={customerName}
      startIcon={
        <Avatar
          size="xs"
          username={customerName}
          imageUrl={safeCustomerDetails?.avatarUrl}
        />
      }
      onRemove={onRemove}
    />
  );
};
